import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, CheckCircle, ChevronDown, ChevronUp, Phone, Mail, MapPin, ArrowRight, X, ChevronLeft, ChevronRight, ZoomIn, MessageCircle } from 'lucide-react';
import SERVICES_DATA from '../data/servicesData';

/* ── Scroll to top on mount ──────────────────────────────── */
const ScrollToTop = () => {
    useEffect(() => { window.scrollTo(0, 0); }, []);
    return null;
};

/* ── FAQ accordion item ──────────────────────────────────── */
const FAQItem = ({ q, a, accent }) => {
    const [open, setOpen] = useState(false);
    return (
        <div
            className="border-b border-gray-200 cursor-pointer"
            onClick={() => setOpen((o) => !o)}
        >
            <div className="flex items-center justify-between py-5 gap-4">
                <h4 className="text-gray-900 font-medium text-base">{q}</h4>
                <span style={{ color: accent }} className="flex-shrink-0">
                    {open ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </span>
            </div>
            {open && (
                <motion.p
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="text-gray-500 text-sm leading-relaxed pb-5"
                >
                    {a}
                </motion.p>
            )}
        </div>
    );
};

/* ── Lightbox ────────────────────────────────────────────── */
const Lightbox = ({ images, index, onClose, onPrev, onNext }) => {
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        const handler = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft') onPrev();
            if (e.key === 'ArrowRight') onNext();
        };
        window.addEventListener('keydown', handler);
        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', handler);
        };
    }, [onClose, onPrev, onNext]);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[99999] flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.95)', backdropFilter: 'blur(10px)' }}
            onClick={onClose}
        >
            {/* close */}
            <button onClick={onClose} className="absolute top-5 right-5 z-10 w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white hover:border-yellow-400 hover:text-yellow-400 transition-colors">
                <X size={20} />
            </button>
            {/* prev */}
            <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className="absolute left-4 md:left-8 z-10 w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white hover:border-yellow-400 hover:text-yellow-400 transition-colors">
                <ChevronLeft size={24} />
            </button>
            {/* image */}
            <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.92 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className="relative flex flex-col items-center mx-20 md:mx-32 max-w-5xl w-full"
                onClick={(e) => e.stopPropagation()}
            >
                <img
                    src={images[index]}
                    alt=""
                    className="w-full max-h-[80vh] object-contain"
                    style={{ boxShadow: '0 0 100px rgba(212,175,55,0.12)' }}
                />
                <p className="mt-4 text-gray-600 text-xs tracking-widest uppercase">
                    {index + 1} &nbsp;/&nbsp; {images.length}
                </p>
            </motion.div>
            {/* next */}
            <button onClick={(e) => { e.stopPropagation(); onNext(); }} className="absolute right-4 md:right-8 z-10 w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white hover:border-yellow-400 hover:text-yellow-400 transition-colors">
                <ChevronRight size={24} />
            </button>
            {/* strip */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                {images.map((_, i) => (
                    <div key={i} className="w-1.5 h-1.5 rounded-full transition-all" style={{ background: i === index ? '#D4AF37' : 'rgba(255,255,255,0.2)' }} />
                ))}
            </div>
        </motion.div>
    );
};

/* ── Gallery Section ─────────────────────────────────────── */
const GRID_AREAS = [
    { area: 'a', style: { gridArea: 'a' } },
    { area: 'b', style: { gridArea: 'b' } },
    { area: 'c', style: { gridArea: 'c' } },
    { area: 'd', style: { gridArea: 'd' } },
    { area: 'e', style: { gridArea: 'e' } },
    { area: 'f', style: { gridArea: 'f' } },
];

const GallerySection = ({ images, accent }) => {
    const [lightbox, setLightbox] = useState(null);
    const close = useCallback(() => setLightbox(null), []);
    const prev = useCallback(() => setLightbox((i) => (i - 1 + images.length) % images.length), [images.length]);
    const next = useCallback(() => setLightbox((i) => (i + 1) % images.length), [images.length]);

    return (
        <section className="bg-black py-14 md:py-24">
            {/* header */}
            <div className="max-w-7xl mx-auto px-5 md:px-6 mb-8 md:mb-10 flex flex-col md:flex-row md:items-end justify-between gap-3 md:gap-4">
                <div>
                    <p className="uppercase tracking-[0.3em] text-xs font-semibold mb-2" style={{ color: accent }}>Project Gallery</p>
                    <h2 className="font-serif text-3xl md:text-5xl text-white leading-tight">
                        Our <em className="not-italic" style={{ color: accent }}>Work</em>
                    </h2>
                </div>
                <p className="text-gray-600 text-sm max-w-xs leading-relaxed">
                    Every photo tells the story of a space transformed. Tap any image to explore.
                </p>
            </div>

            <div className="max-w-7xl mx-auto px-5 md:px-6">

                {/* ── Mobile layout: clean 2-col with first image full-width ── */}
                <div className="md:hidden flex flex-col gap-1">
                    {/* First image — full width hero */}
                    <div
                        className="relative overflow-hidden cursor-pointer w-full"
                        style={{ height: '220px' }}
                        onClick={() => setLightbox(0)}
                    >
                        <img src={images[0]} alt="" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                        <span className="absolute bottom-3 left-3 text-xs font-bold px-2 py-0.5 rounded" style={{ background: accent, color: '#000' }}>01</span>
                    </div>
                    {/* Remaining images — 2-column grid */}
                    <div className="grid grid-cols-2 gap-1">
                        {images.slice(1).map((src, i) => (
                            <div
                                key={i + 1}
                                className="relative overflow-hidden cursor-pointer"
                                style={{ height: '150px' }}
                                onClick={() => setLightbox(i + 1)}
                            >
                                <img src={src} alt="" className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                                <span className="absolute bottom-2 left-2 text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: accent, color: '#000' }}>
                                    {String(i + 2).padStart(2, '0')}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Desktop layout: editorial mosaic collage ── */}
                <div
                    className="hidden md:grid w-full"
                    style={{
                        gridTemplateColumns: '2fr 1fr 1fr',
                        gridTemplateRows: '320px 220px',
                        gridTemplateAreas: `
                            "a b c"
                            "a d e"
                        `,
                        gap: '4px',
                    }}
                >
                    {images.slice(0, 5).map((src, i) => (
                        <GalleryTile
                            key={i}
                            src={src}
                            index={i}
                            style={GRID_AREAS[i].style}
                            accent={accent}
                            onClick={() => setLightbox(i)}
                        />
                    ))}
                </div>

                {/* bottom row — remaining images (desktop only) */}
                {images.length > 5 && (
                    <div className="hidden md:grid grid-cols-3 gap-1 mt-1">
                        {images.slice(5).map((src, i) => (
                            <GalleryTile
                                key={i + 5}
                                src={src}
                                index={i + 5}
                                style={{ height: '180px' }}
                                accent={accent}
                                onClick={() => setLightbox(i + 5)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* lightbox */}
            <AnimatePresence>
                {lightbox !== null && (
                    <Lightbox images={images} index={lightbox} onClose={close} onPrev={prev} onNext={next} />
                )}
            </AnimatePresence>
        </section>
    );
};

const GalleryTile = ({ src, index, style, accent, onClick }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: index * 0.07 }}
            className="relative overflow-hidden cursor-pointer"
            style={style}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={onClick}
        >
            <img
                src={src}
                alt=""
                className="w-full h-full object-cover transition-transform duration-700"
                style={{ transform: hovered ? 'scale(1.07)' : 'scale(1)' }}
            />
            {/* permanent gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            {/* hover overlay */}
            <motion.div
                animate={{ opacity: hovered ? 1 : 0 }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0 flex items-center justify-center"
                style={{ background: `${accent}18`, backdropFilter: hovered ? 'blur(1px)' : 'none' }}
            >
                <div
                    className="w-14 h-14 rounded-full flex items-center justify-center border-2 border-white/80"
                    style={{ background: 'rgba(0,0,0,0.4)' }}
                >
                    <ZoomIn size={22} color="white" />
                </div>
            </motion.div>
            {/* index badge bottom-left */}
            <span
                className="absolute bottom-3 left-3 text-xs font-bold px-2 py-0.5 rounded"
                style={{ background: accent, color: '#000' }}
            >
                {String(index + 1).padStart(2, '0')}
            </span>
        </motion.div>
    );
};

/* ── WA helper ──────────────────────────────────────────── */
const WA_NUMBER = '971581735200';
const buildWaLink = (service, name, msg) => {
    const text = [
        `Hello Royal Standard! 👋`,
        name ? `My name is ${name}.` : '',
        `I'm interested in: *${service}*.`,
        msg ? `\n${msg}` : '',
        '\nLooking forward to hearing from you.',
    ].filter(Boolean).join('\n');
    return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(text)}`;
};

/* ── Main Page ───────────────────────────────────────────── */
const ServicePage = () => {
    const { slug } = useParams();
    const [waName, setWaName] = useState('');
    const [waMsg, setWaMsg] = useState('');
    const s = SERVICES_DATA[slug];

    if (!s) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center">
                <h1 className="text-3xl font-serif text-gray-800 mb-4">Service not found</h1>
                <Link to="/" className="text-yellow-600 underline">← Back to Home</Link>
            </div>
        );
    }

    return (
        <div className="font-sans bg-white min-h-screen">
            <ScrollToTop />

            {/* ── Back navigation bar ── */}
            <div
                className="fixed top-0 left-0 w-full z-50 bg-black/90 backdrop-blur-sm"
                style={{ borderBottom: '1px solid rgba(212,175,55,0.15)' }}
            >
                {/* Desktop: single row with space-between */}
                <div className="hidden md:flex items-center justify-between px-8 py-4">
                    <Link
                        to="/"
                        className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-xs uppercase tracking-widest"
                    >
                        <ArrowLeft size={15} /> Back to Home
                    </Link>
                    <span className="flex items-center gap-2 leading-none">
                        <span className="font-serif text-base text-white tracking-widest">ROYAL</span>
                        <img src="/logo.png" alt="Royal Standard Logo" style={{ height: '28px', width: 'auto', objectFit: 'contain', display: 'block' }} />
                        <span className="font-serif text-base tracking-widest" style={{ color: '#D4AF37' }}>STANDARD</span>
                    </span>
                    <a
                        href="#contact-section"
                        className="text-xs uppercase tracking-widest px-5 py-2.5 font-semibold text-black transition-opacity hover:opacity-90"
                        style={{ background: s.accent }}
                    >
                        Get a Quote
                    </a>
                </div>

                {/* Mobile: compact logo row + full-width CTA */}
                <div className="md:hidden">
                    <div className="flex items-center justify-between px-4 py-3">
                        <Link
                            to="/"
                            className="flex items-center gap-1.5 text-white/70 hover:text-white transition-colors text-xs uppercase tracking-wider"
                        >
                            <ArrowLeft size={14} />
                            <span className="hidden xs:inline">Back</span>
                        </Link>
                        <span className="flex items-center gap-1.5 leading-none">
                            <span className="font-serif text-sm text-white tracking-widest">ROYAL</span>
                            <img src="/logo.png" alt="Royal Standard Logo" style={{ height: '22px', width: 'auto', objectFit: 'contain', display: 'block' }} />
                            <span className="font-serif text-sm tracking-widest" style={{ color: '#D4AF37' }}>STANDARD</span>
                        </span>
                        <div className="w-8" /> {/* spacer to keep logo centered */}
                    </div>
                    <a
                        href="#contact-section"
                        className="flex items-center justify-center gap-2 w-full py-2.5 text-black text-xs uppercase tracking-widest font-semibold"
                        style={{ background: s.accent }}
                    >
                        Get a Free Quote <ArrowRight size={12} />
                    </a>
                </div>
            </div>

            {/* ── Hero ── */}
            <section className="relative h-screen flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0">
                    <img src={s.heroImage} alt={s.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/55" />
                    <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, transparent 40%, ${s.accent}22 100%)` }} />
                </div>
                <div className="relative z-10 text-center px-6 max-w-4xl">
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="uppercase tracking-[0.3em] text-xs font-semibold mb-4"
                        style={{ color: s.accent }}
                    >
                        Royal Standard · Premium Service
                    </motion.p>
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15 }}
                        className="font-serif text-5xl md:text-7xl text-white mb-6 leading-tight"
                    >
                        {s.title}
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="text-gray-200 text-xl md:text-2xl font-light max-w-2xl mx-auto mb-10"
                    >
                        {s.tagline}
                    </motion.p>
                    <motion.a
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.45 }}
                        href="#overview"
                        className="inline-flex items-center gap-2 text-sm uppercase tracking-widest px-8 py-4 font-semibold text-black"
                        style={{ background: s.accent }}
                    >
                        Discover More <ArrowRight size={14} />
                    </motion.a>
                </div>
                {/* scroll cue */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/40">
                    <span className="text-xs uppercase tracking-widest">Scroll</span>
                    <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.4 }}>
                        <ChevronDown size={18} />
                    </motion.span>
                </div>
            </section>

            {/* ── Overview ── */}
            <section id="overview" className="py-24 bg-white">
                <div className="max-w-6xl mx-auto px-6 flex flex-col lg:flex-row gap-16 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        className="lg:w-1/2"
                    >
                        <p className="uppercase tracking-widest text-xs font-semibold mb-3" style={{ color: s.accent }}>Overview</p>
                        <h2 className="font-serif text-4xl text-gray-900 mb-6 leading-tight">What We Do</h2>
                        <div className="w-14 h-px mb-6" style={{ background: s.accent }} />
                        <p className="text-gray-600 text-lg leading-relaxed">{s.overview}</p>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        className="lg:w-1/2 grid grid-cols-2 gap-3"
                    >
                        {s.gallery.slice(0, 4).map((img, i) => (
                            <div key={i} className={`overflow-hidden rounded-sm shadow-md ${i === 0 ? 'col-span-2 h-56' : 'h-36'}`}>
                                <img src={img} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                            </div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* ── Process ── */}
            <section className="py-24" style={{ background: '#111' }}>
                <div className="max-w-6xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <p className="uppercase tracking-widest text-xs font-semibold mb-3" style={{ color: s.accent }}>How We Work</p>
                        <h2 className="font-serif text-4xl text-white">Our Process</h2>
                        <div className="w-14 h-px mx-auto mt-5" style={{ background: s.accent }} />
                    </div>
                    <div className="relative">
                        {/* connector line */}
                        <div className="hidden md:block absolute top-8 left-0 right-0 h-px" style={{ background: `${s.accent}40` }} />
                        <div
                            className="grid gap-8"
                            style={{
                                gridTemplateColumns: `repeat(1, 1fr)`,
                            }}
                            ref={el => {
                                if (!el) return;
                                const mq = window.matchMedia('(min-width: 768px)');
                                const apply = (e) => {
                                    el.style.gridTemplateColumns = e.matches
                                        ? `repeat(${s.process.length}, 1fr)`
                                        : 'repeat(1, 1fr)';
                                };
                                apply(mq);
                                mq.addEventListener('change', apply);
                            }}
                        >
                            {s.process.map((p, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.1 }}
                                    className="flex flex-col items-center text-center relative"
                                >
                                    <div
                                        className="w-16 h-16 rounded-full flex items-center justify-center text-black font-serif text-lg font-bold mb-5 z-10"
                                        style={{ background: s.accent }}
                                    >
                                        {p.step}
                                    </div>
                                    <h4 className="text-white font-serif text-lg mb-2">{p.title}</h4>
                                    <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Benefits ── */}
            <section className="py-24 bg-gray-50">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="flex flex-col lg:flex-row gap-16 items-start">
                        <div className="lg:w-2/5">
                            <p className="uppercase tracking-widest text-xs font-semibold mb-3" style={{ color: s.accent }}>Why Choose Us</p>
                            <h2 className="font-serif text-4xl text-gray-900 leading-tight mb-6">What Sets Royal Standard Apart</h2>
                            <div className="w-14 h-px mb-6" style={{ background: s.accent }} />
                            <p className="text-gray-500 leading-relaxed">
                                Every project we undertake is backed by years of expertise, premium materials and a commitment to delivering results that speak for themselves.
                            </p>
                            <a
                                href="#contact-section"
                                className="inline-flex items-center gap-2 mt-8 text-sm uppercase tracking-widest px-7 py-3 font-semibold text-black transition-all"
                                style={{ background: s.accent }}
                            >
                                Get a Free Quote <ArrowRight size={14} />
                            </a>
                        </div>
                        <div className="lg:w-3/5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {s.benefits.map((b, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, x: 20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.08 }}
                                    className="flex items-start gap-3 bg-white p-5 rounded-sm shadow-sm border border-gray-100"
                                >
                                    <CheckCircle size={20} className="flex-shrink-0 mt-0.5" style={{ color: s.accent }} />
                                    <span className="text-gray-700 text-sm leading-relaxed">{b}</span>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Gallery ── */}
            <GallerySection images={s.gallery} accent={s.accent} />

            {/* ── FAQ ── */}
            <section className="py-24 bg-gray-50">
                <div className="max-w-3xl mx-auto px-6">
                    <div className="text-center mb-12">
                        <p className="uppercase tracking-widest text-xs font-semibold mb-3" style={{ color: s.accent }}>FAQ</p>
                        <h2 className="font-serif text-4xl text-gray-900">Common Questions</h2>
                        <div className="w-14 h-px mx-auto mt-5" style={{ background: s.accent }} />
                    </div>
                    <div className="divide-y divide-gray-200">
                        {s.faq.map((item, i) => (
                            <FAQItem key={i} q={item.q} a={item.a} accent={s.accent} />
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Contact CTA ── */}
            <section id="contact-section" className="py-24" style={{ background: '#111' }}>
                <div className="max-w-6xl mx-auto px-6">

                    {/* header */}
                    <div className="text-center mb-14">
                        <p className="uppercase tracking-widest text-xs font-semibold mb-3" style={{ color: s.accent }}>Ready to Start?</p>
                        <h2 className="font-serif text-4xl md:text-5xl text-white leading-tight">
                            Let's Start on <em className="not-italic" style={{ color: '#25D366' }}>WhatsApp</em>
                        </h2>
                        <div className="w-14 h-px mx-auto mt-5" style={{ background: s.accent }} />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

                        {/* Left */}
                        <div className="lg:col-span-2 flex flex-col gap-6">

                            {/* WhatsApp CTA card */}
                            <div
                                className="rounded-sm p-7 flex flex-col items-center text-center gap-4"
                                style={{ background: 'linear-gradient(135deg,rgba(18,140,126,0.15),rgba(37,211,102,0.12))', border: '1px solid rgba(37,211,102,0.3)' }}
                            >
                                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: '#25D366' }}>
                                    <MessageCircle size={28} color="#fff" />
                                </div>
                                <div>
                                    <h3 className="font-serif text-xl text-white mb-1">WhatsApp Us Directly</h3>
                                    <p className="text-gray-500 text-sm leading-relaxed">Fastest way to reach us — responses within 60 minutes.</p>
                                </div>
                                <a
                                    href={`https://wa.me/${WA_NUMBER}`}
                                    target="_blank" rel="noreferrer"
                                    className="w-full flex items-center justify-center gap-2 py-3 rounded-full text-white text-sm font-semibold transition-all hover:brightness-110"
                                    style={{ background: '#25D366', boxShadow: '0 6px 24px rgba(37,211,102,0.3)' }}
                                >
                                    <MessageCircle size={16} /> +971 58 173 5200
                                </a>
                            </div>

                            {/* Contact details */}
                            <div className="rounded-sm p-6 space-y-5" style={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.07)' }}>
                                {[
                                    { icon: <Phone size={16} />, label: 'Phone', value: '+971 58 173 5200', href: 'tel:+971581735200' },
                                    { icon: <Mail size={16} />, label: 'Email', value: 'info@royalstandarduae.com', href: 'mailto:info@royalstandarduae.com' },
                                    { icon: <MapPin size={16} />, label: 'Location', value: 'Dubai, United Arab Emirates', href: null },
                                ].map((c, i) => (
                                    <div key={i} className="flex items-center gap-4">
                                        <div className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: 'rgba(212,175,55,0.1)', color: s.accent }}>
                                            {c.icon}
                                        </div>
                                        <div>
                                            <p className="text-gray-400 text-[10px] uppercase tracking-widest">{c.label}</p>
                                            {c.href
                                                ? <a href={c.href} className="text-gray-300 text-sm hover:text-yellow-400 transition-colors">{c.value}</a>
                                                : <p className="text-gray-300 text-sm">{c.value}</p>
                                            }
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Right: message builder */}
                        <div className="lg:col-span-3 rounded-sm p-8 md:p-10" style={{ background: '#1a1a1a', border: '1px solid rgba(212,175,55,0.12)' }}>
                            <p className="uppercase tracking-widest text-xs font-semibold mb-2" style={{ color: s.accent }}>Message Builder</p>
                            <h3 className="font-serif text-2xl text-white mb-1">Send via WhatsApp</h3>
                            <p className="text-gray-500 text-sm mb-7">Fill in the details — your message will open WhatsApp pre-written and ready to send.</p>

                            <div className="space-y-5">
                                {/* Service (pre-filled, read-only) */}
                                <div>
                                    <label className="block text-[10px] text-gray-400 uppercase tracking-widest mb-1.5">Service</label>
                                    <input
                                        type="text" readOnly value={s.title}
                                        className="w-full bg-[#111] border border-gray-700 px-4 py-3 text-sm text-gray-500"
                                    />
                                </div>

                                {/* Name */}
                                <div>
                                    <label className="block text-[10px] text-gray-500 uppercase tracking-widest mb-1.5">Your Name</label>
                                    <input
                                        type="text" value={waName} onChange={(e) => setWaName(e.target.value)}
                                        placeholder="e.g. Ahmed Al Mansoori"
                                        className="w-full bg-[#111] border border-gray-700 px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-yellow-600 transition-colors"
                                    />
                                </div>

                                {/* Message */}
                                <div>
                                    <label className="block text-[10px] text-gray-500 uppercase tracking-widest mb-1.5">Project Details</label>
                                    <textarea
                                        rows={4} value={waMsg} onChange={(e) => setWaMsg(e.target.value)}
                                        placeholder="Describe your space, requirements or any questions…"
                                        className="w-full bg-[#111] border border-gray-700 px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-yellow-600 transition-colors resize-none"
                                    />
                                </div>

                                {/* Live preview */}
                                {(waName || waMsg) && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                                        className="rounded p-4"
                                        style={{ background: 'rgba(37,211,102,0.06)', border: '1px solid rgba(37,211,102,0.2)' }}
                                    >
                                        <p className="text-green-600 text-[10px] uppercase tracking-widest mb-2">Message Preview</p>
                                        <p className="text-gray-600 text-xs leading-relaxed whitespace-pre-line">
                                            {[
                                                `Hello Royal Standard! 👋`,
                                                waName ? `My name is ${waName}.` : '',
                                                `I'm interested in: *${s.title}*.`,
                                                waMsg ? `\n${waMsg}` : '',
                                                '\nLooking forward to hearing from you.',
                                            ].filter(Boolean).join('\n')}
                                        </p>
                                    </motion.div>
                                )}

                                {/* CTA */}
                                <a
                                    href={buildWaLink(s.title, waName, waMsg)}
                                    target="_blank" rel="noreferrer"
                                    className="flex items-center justify-center gap-3 w-full py-4 text-white text-sm uppercase tracking-widest font-semibold rounded-full transition-all hover:brightness-110"
                                    style={{ background: '#25D366', boxShadow: '0 6px 28px rgba(37,211,102,0.25)' }}
                                >
                                    <MessageCircle size={18} />
                                    WHATSAPP US
                                    <ArrowRight size={15} />
                                </a>

                                <p className="text-gray-400 text-[11px] text-center">
                                    WhatsApp will open with your message pre-filled.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Other Services ── */}
            <section className="py-16 bg-white border-t border-gray-100">
                <div className="max-w-6xl mx-auto px-6">
                    <p className="uppercase tracking-widest text-xs font-semibold mb-8 text-center text-gray-400">Explore Our Other Services</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {Object.values(SERVICES_DATA)
                            .filter((sv) => sv.slug !== s.slug)
                            .map((sv) => (
                                <Link
                                    key={sv.slug}
                                    to={`/services/${sv.slug}`}
                                    className="group relative overflow-hidden rounded-sm h-40 block"
                                >
                                    <img src={sv.heroImage} alt={sv.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                    <div className="absolute inset-0 bg-black/50 flex items-end p-4">
                                        <span className="text-white font-serif text-sm leading-tight group-hover:text-yellow-400 transition-colors">{sv.title}</span>
                                    </div>
                                </Link>
                            ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ServicePage;
